import { useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useNavigate,
} from 'react-router';
import styles from './AppRouter.module.css';
import { Layout } from '#/components/Layout';
import { getAuth0Config, type Auth0Config, type Auth0ConfigResult } from '#/config';
import { Login } from '#/pages/Login/Login';
import { Result } from '#/pages/Result/Result';
import { AuthProvider } from '#/utils/auth/AuthProvider';
import { createAuth0Client } from '#/utils/auth/auth0Client';

function RouteError() {
  return (
    <div className={styles.card} role="alert">
      <h1 className={styles.title}>画面の表示中にエラーが発生しました</h1>
      <p className={styles.description}>お手数ですが、ページを再読み込みしてからもう一度お試しください。</p>
    </div>
  );
}

function ConfigurationError({ missingKeys }: { missingKeys: string[] }) {
  return (
    <div className={styles.card} role="alert">
      <h1 className={styles.title}>ログイン確認アプリの設定が完了していません</h1>
      <p className={styles.description}>必要な環境変数が設定されていないため、ログイン確認を開始できません。</p>
      <dl className={styles.list}>
        <dt>未設定項目</dt>
        <dd>{missingKeys.join(', ')}</dd>
      </dl>
    </div>
  );
}

function AuthenticatedLayout({ config }: { config: Auth0Config }) {
  const navigate = useNavigate();
  const auth0Client = useMemo(() => createAuth0Client(config), [config]);
  const handleRedirectCallback = useCallback(() => {
    navigate(window.location.pathname, { replace: true });
  }, [navigate]);

  return (
    <AuthProvider client={auth0Client} onRedirectCallback={handleRedirectCallback}>
      <ErrorBoundary fallback={<RouteError />}>
        <Outlet />
      </ErrorBoundary>
    </AuthProvider>
  );
}

function AppLayout({ configResult }: { configResult: Auth0ConfigResult }) {
  return (
    <Layout>
      {configResult.ok ? (
        <AuthenticatedLayout config={configResult.config} />
      ) : (
        <ConfigurationError missingKeys={configResult.missingKeys} />
      )}
    </Layout>
  );
}

export function createAppRouter(configResult: Auth0ConfigResult = getAuth0Config()) {
  return createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<AppLayout configResult={configResult} />}>
        <Route index element={<Login />} />
        <Route path="result" element={<Result />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    )
  );
}

export function AppRouter() {
  const router = useMemo(() => createAppRouter(), []);
  return <RouterProvider router={router} />;
}
